import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { z } from 'zod'

import styles from './CreateCodesDialog.module.scss'

import { isApiError } from '@/api'
import type { CreateCodesRequest } from '@/api/generated'
import { createPromoCodes } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormField from '@/components/FormField/FormField'

const UNAMBIGUOUS_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'

type CreationMode = 'single' | 'bulk'

function generateRandomCodes(count: number, prefix?: string): string[] {
  const codes = new Set<string>()
  while (codes.size < count) {
    let randomPart = ''
    for (let charIndex = 0; charIndex < 8; charIndex++) {
      randomPart += UNAMBIGUOUS_CHARS[Math.floor(Math.random() * UNAMBIGUOUS_CHARS.length)]
    }
    codes.add(prefix ? `${prefix}-${randomPart}` : randomPart)
  }
  return Array.from(codes)
}

const singleCodeSchema = z.object({
  code: z.string().min(1, 'Code is required').max(80, 'Code must be 80 characters or fewer'),
  maxRedemptions: z
    .string()
    .min(1, 'Required')
    .refine(
      (countStr) => {
        const redemptionLimit = Number(countStr)
        return Number.isInteger(redemptionLimit) && redemptionLimit >= 1
      },
      'Must be a positive integer',
    ),
})

const bulkCodesSchema = z.object({
  count: z
    .string()
    .min(1, 'Required')
    .refine(
      (countStr) => {
        const codeCount = Number(countStr)
        return Number.isInteger(codeCount) && codeCount >= 1 && codeCount <= 10_000
      },
      'Must be between 1 and 10,000',
    ),
  usePrefix: z.boolean(),
  prefix: z.string().optional(),
})

type SingleCodeFormValues = z.infer<typeof singleCodeSchema>
type BulkCodesFormValues = z.infer<typeof bulkCodesSchema>

interface CodeFormProps {
  campaignId: string
  onSave: (createdCodeStrings: string[]) => void
  onCancel: () => void
}

function useCreateCodesMutation(campaignId: string, onSave: (codes: string[]) => void, resetForm: () => void) {
  const [formError, setFormError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (codesRequest: CreateCodesRequest) => {
      await createPromoCodes(campaignId, codesRequest)
      return codesRequest.codes
    },
    onSuccess: (submittedCodes) => {
      queryClient.invalidateQueries({ queryKey: ['promoCodes', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['promoCampaigns'] })
      resetForm()
      onSave(submittedCodes)
    },
    onError: (err) => {
      if (isApiError(err)) {
        const errorDetail = typeof err.detail === 'string' ? err.detail : 'Failed to create codes'
        setFormError(errorDetail)
      } else {
        setFormError('An unexpected error occurred')
      }
    },
  })

  return { mutation, formError, setFormError }
}

const SingleCodeForm = ({ campaignId, onSave, onCancel }: CodeFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SingleCodeFormValues>({
    resolver: zodResolver(singleCodeSchema),
    defaultValues: { code: '', maxRedemptions: '' },
  })

  const { mutation, formError, setFormError } = useCreateCodesMutation(campaignId, onSave, reset)

  const onSubmit = (formValues: SingleCodeFormValues) => {
    setFormError(null)
    mutation.mutate({
      codes: [formValues.code.trim().toUpperCase()],
      max_redemptions: Number(formValues.maxRedemptions),
    })
  }

  return (
    <form className={styles.formContent} onSubmit={handleSubmit(onSubmit)}>
      {formError && <div className={styles.errorBanner}>{formError}</div>}

      <FormField
        name="code"
        label="Promo code"
        control={control}
        error={errors.code}
        placeholder="SUMMER2026"
        disabled={mutation.isPending}
      />

      <FormField
        name="maxRedemptions"
        label="Max redemptions"
        type="number"
        control={control}
        error={errors.maxRedemptions}
        placeholder="2000"
        disabled={mutation.isPending}
      />

      <div className={styles.dialogFooter}>
        <Button title="Cancel" onClick={onCancel} type="light" />
        <Button
          title={mutation.isPending ? 'Creating...' : 'Create code'}
          onClick={handleSubmit(onSubmit)}
          type="dark"
          isDisabled={mutation.isPending}
        />
      </div>
    </form>
  )
}

const BulkCodesForm = ({ campaignId, onSave, onCancel }: CodeFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BulkCodesFormValues>({
    resolver: zodResolver(bulkCodesSchema),
    defaultValues: { count: '', usePrefix: false, prefix: '' },
  })

  const { mutation, formError, setFormError } = useCreateCodesMutation(campaignId, onSave, reset)

  const isPrefixEnabled = watch('usePrefix')
  const bulkCountValue = watch('count')
  const prefixValue = watch('prefix')

  const onSubmit = (formValues: BulkCodesFormValues) => {
    setFormError(null)
    const codeCount = Number(formValues.count)
    const trimmedPrefix =
      formValues.usePrefix && formValues.prefix?.trim() ? formValues.prefix.trim().toUpperCase() : undefined
    const generatedCodes = generateRandomCodes(codeCount, trimmedPrefix)
    mutation.mutate({
      codes: generatedCodes,
      max_redemptions: 1,
    })
  }

  const samplePrefix = isPrefixEnabled && prefixValue?.trim() ? prefixValue.trim().toUpperCase() : undefined
  const sampleCode = samplePrefix ? `${samplePrefix}-A7K9F2M3` : 'A7K9F2M3'

  const enteredBulkCount = Number(bulkCountValue)
  const hasValidBulkCount = Number.isInteger(enteredBulkCount) && enteredBulkCount > 0

  return (
    <form className={styles.formContent} onSubmit={handleSubmit(onSubmit)}>
      {formError && <div className={styles.errorBanner}>{formError}</div>}

      <FormField
        name="count"
        label="Number of codes"
        type="number"
        control={control}
        error={errors.count}
        placeholder="2000"
        disabled={mutation.isPending}
      />

      <div className={styles.prefixSection}>
        <Controller
          name="usePrefix"
          control={control}
          render={({ field }) => (
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={field.value}
                onChange={field.onChange}
                disabled={mutation.isPending}
              />
              Add prefix to all codes
            </label>
          )}
        />

        {isPrefixEnabled && (
          <FormField
            name="prefix"
            label="Prefix"
            control={control}
            error={errors.prefix}
            placeholder="ALEXBANKS"
            required={false}
            disabled={mutation.isPending}
          />
        )}
      </div>

      <div className={styles.samplePreview}>
        Sample: <code>{sampleCode}</code> (single-use)
      </div>

      <div className={styles.dialogFooter}>
        <Button title="Cancel" onClick={onCancel} type="light" />
        <Button
          title={
            mutation.isPending
              ? 'Generating...'
              : hasValidBulkCount
                ? `Generate ${enteredBulkCount.toLocaleString()} code${enteredBulkCount === 1 ? '' : 's'}`
                : 'Generate codes'
          }
          onClick={handleSubmit(onSubmit)}
          type="dark"
          isDisabled={mutation.isPending}
        />
      </div>
    </form>
  )
}

interface CreateCodesDialogProps {
  isOpen: boolean
  campaignId: string
  onSave: (createdCodeStrings: string[]) => void
  onCancel: () => void
}

const CreateCodesDialog = ({ isOpen, campaignId, onSave, onCancel }: CreateCodesDialogProps) => {
  const [creationMode, setCreationMode] = useState<CreationMode>('single')

  return (
    <DialogWrapper isOpen={isOpen} title="Add Codes" close={onCancel} minWidth={480}>
      <div className={styles.dialogBody}>
        <div className={styles.modeToggle}>
          <button
            type="button"
            className={clsx(styles.modeButton, creationMode === 'single' && styles.modeActive)}
            onClick={() => setCreationMode('single')}
          >
            Single Code
          </button>
          <button
            type="button"
            className={clsx(styles.modeButton, creationMode === 'bulk' && styles.modeActive)}
            onClick={() => setCreationMode('bulk')}
          >
            Generate Bulk
          </button>
        </div>

        {creationMode === 'single' ? (
          <SingleCodeForm campaignId={campaignId} onSave={onSave} onCancel={onCancel} />
        ) : (
          <BulkCodesForm campaignId={campaignId} onSave={onSave} onCancel={onCancel} />
        )}
      </div>
    </DialogWrapper>
  )
}

export default CreateCodesDialog
